import { useContext } from 'react';
import { isCompletedTimeFormat } from '@ems/client-design-system';
import { OperationStateContext } from 'src/@operations/providers/OperationsStateProvider';
import { useSelectors } from 'src/utils/storeHelpers';
import { IFilterState, IFilterSelectors, IFilterItem, IRangeFilters } from 'src/@operations/props';
import { actionTypes } from 'src/@operations/newActionTypes';
import {
  letterCaseFormat,
  storeFilterReducerRow,
  readFilterReducerRow,
  storeFilterReducerRangeItem,
  readFilterReducerRangeItem,
  clearFilterReducerRow,
  clearFilterReducerRangeItem,
  ignoreSavedFilters,
} from 'src/utils';
import { OPERATION, NULL_VALUE } from 'src/constants';

export const useFilterSelectors: () => IFilterSelectors = () => {
  const state: any = useContext(OperationStateContext);
  const filterState: IFilterState = state.filter;

  return useSelectors(filterState, (state: IFilterState) => ({
    getIfInitialised: () => state.isInitialised,
    getRegularFilters: () => state.tableFilters,
    getTimeValues: () => state.time,
    getTimeFilter: () => {
      const { from, to } = state.time;
      const fromComplete = from && isCompletedTimeFormat(from);
      const toComplete = to && isCompletedTimeFormat(to);
      let label = '';
      if (fromComplete || toComplete) {
        label = `${from}-${to}`;
      }

      return {
        value: state.time,
        label,
      };
    },
    getIfFilterApplied: () => {
      let isFiltered = false;
      Object.entries(state.tableFilters).forEach(([, value]) => {
        if (value.length) {
          isFiltered = true;
        }
      });
      if (state.time.from !== '' && state.time.to !== '') {
        isFiltered = true;
      }
      return isFiltered;
    },
    getFilterString: () => {
      // Manually formats the selected filters to fit what is required
      // Unlike JSON.stringify, the key side needs to be unquoted
      // For operation types and aircraft categories, each value in the array must be unquoted as well,
      // as they are enum types in the back-end.
      // Example outputs: {runwayNames: ["04L"], aircraftCategories: [BusinessJet]}
      // OR {} (initially)
      let filterString = '';
      Object.entries(state.tableFilters).map(([key, value]) => {
        const valuesToFormat = ['operationTypes', 'aircraftCategories'];

        const selectedKeys = value.reduce((acc: Array<string | null>, current: IFilterItem) => {
          const currentKey = current.key === NULL_VALUE ? null : letterCaseFormat(key, current.key);
          return acc.concat(currentKey);
        }, []);
        if (selectedKeys.length > 0) {
          // Add comma if it's not the first filter to be added
          if (filterString !== '') {
            filterString += ', ';
          }

          const values = JSON.stringify(selectedKeys);
          const formattedValues = valuesToFormat.includes(key) ? values.replace(/\"/g, '') : values;
          if (key === 'correlated') {
            selectedKeys.map((item, index) => {
              if (index > 0) {
                filterString += ', ';
              }
              // TODO: clean this up by having the correct filter keys in the filter-data file
              if (item === null) {
                filterString += 'hasCorrelations: false';
              } else if (item === 'NoiseEvents') {
                filterString += `has${item}: true`;
              } else if (item === 'CCO' || item === 'CDO') {
                filterString += `has${item.charAt(0).toUpperCase() +
                  item.slice(1).toLowerCase()}Infringements: true`;
              } else if (item === 'Infringements') {
                filterString += 'hasInfringements: true';
              } else {
                filterString += `has${item}Infringements: true`;
              }
            });
          } else {
            filterString += `${key}: ${formattedValues}`;
          }
        }
      });

      const { from: timeFrom, to: timeTo } = state.time;
      const fromComplete = timeFrom && isCompletedTimeFormat(timeFrom);
      const toComplete = timeTo && isCompletedTimeFormat(timeTo);
      if (fromComplete && toComplete) {
        const fromSplit = timeFrom.split(':');
        const toSplit = timeTo.split(':');
        const fromInSeconds = Number(fromSplit[0]) * 60 * 60 + Number(fromSplit[1]) * 60;
        const toInSeconds = Number(toSplit[0]) * 60 * 60 + Number(toSplit[1]) * 60;
        if (filterString !== '') {
          filterString += ', '; // Add comma if it's not the first filter to be added
        }
        filterString += `, timePeriod: {start: ${fromInSeconds}, end: ${toInSeconds}}`;
      }

      return `filter: {${filterString}}`;
    },
  }));
};

const initialRangeFilters: IRangeFilters = {
  from: '',
  to: '',
};

const initialStateObj: IFilterState = {
  isInitialised: false,
  tableFilters: {
    operationTypes: [],
    runwayNames: [],
    airportIds: [],
    remoteAirportIds: [],
    aircraftTypes: [],
    airlines: [],
    correlated: [],
    aircraftCategories: [],
  },
  time: initialRangeFilters,
};

export const filterInitialState: IFilterState = Object.assign({}, initialStateObj);

export const filterReducer = (state: IFilterState, action: any) => {
  switch (action.type) {
    case actionTypes.UPDATE_RANGE_FILTER: {
      const { type, value, field } = action.data;
      const newValue = {
        from: field === 'from' ? value : state[type].from,
        to: field === 'to' ? value : state[type].to,
      };

      storeFilterReducerRangeItem(OPERATION, type, newValue);
      return Object.assign({}, state, {
        [type]: newValue,
      });
    }
    case actionTypes.UPDATE_SELECTED_ITEMS: {
      const { category, selectedItems } = action.data;
      const newState = Object.assign({}, state, {
        tableFilters: {
          ...state.tableFilters,
          [category]: selectedItems,
        },
      });

      storeFilterReducerRow(OPERATION, newState.tableFilters);
      return newState;
    }
    case actionTypes.CLEAR_SELECTED_ITEMS:
      clearFilterReducerRow(OPERATION);
      clearFilterReducerRangeItem(OPERATION, 'time');
      return Object.assign({}, initialStateObj, {
        isInitialised: true,
      });
    case actionTypes.INITIALISE_STORE:
      const { initialFilters } = action.data;

      const savedFiltersRow = readFilterReducerRow(OPERATION);
      const savedTime = readFilterReducerRangeItem(OPERATION, 'time');
      storeFilterReducerRow(OPERATION, { ...initialStateObj.tableFilters, ...initialFilters });
      storeFilterReducerRangeItem(OPERATION, 'time', initialStateObj.time);
      ignoreSavedFilters(OPERATION);
      return Object.assign({}, state, {
        isInitialised: true,
        tableFilters: savedFiltersRow
          ? JSON.parse(savedFiltersRow)
          : { ...initialStateObj.tableFilters, ...initialFilters },
        time: savedTime ? JSON.parse(savedTime) : initialStateObj.time,
      });
    default:
      return state;
  }
};
